<!--
  COTI-502: elemento base Offset (impresión y procesos planos), módulo Cotización.

  Replica la lógica de Cajas sobre `input-suaje` —el otro producto plano—: el suaje aparece
  cuando hay un material que valida ancho, y las dimensiones de ese material alimentan la
  vista de imposición y los mínimos de hoja.

  Dos cosas propias de offset:
   - `mostrarAnchoLargoItem`: Ancho y Largo item son editables y se persisten en
     CotizacionElemento, porque el pliego que entra en máquina puede ser más chico que la
     hoja del proveedor. En Cajas son de sólo lectura.
   - Los `provide` de abajo son el canal por el que `input_materiales` publica el ancho del
     material e `input_suaje` lo lee. Sin `currentModule` ese canal nunca se habilita.

  Los campos visibles y sus etiquetas ("Largo de pliego") vienen del backend, por
  ElementoBaseCampos y ElementoBaseEtiquetas del elemento base 11.
-->
<template>
  <el-collapse v-model="collapseModel">
    <elemento-header
      :title="title"
      :name="name"
    >
      <input-suaje
        v-if="mostrarSuaje"
        v-model="localModel"
        :validate="validarCotizacionCampos"
        v-bind="componentsProps.InputSuaje"
        :mostrarEngranaje="false"
        :mostrarAnchoLargoItem="true"
        :mostrarPliegosPorHoja="true"
        :anchoImposicion="materialRef?.Ancho"
        :largoImposicion="materialRef?.Largo"
        @update:unidadImposicion="onUnidadImposicionChange"
      />

      <el-alert
        v-if="elementoTipo && !tieneProcesoValidaAncho"
        title="Configuración incompleta"
        type="warning"
        show-icon
        :closable="false"
      >
        El sistema no pudo determinar el proceso para validar el ancho de item.<br />
        Comuníquese con soporte.
      </el-alert>
      <input-procesos
        v-model="localModel.Secciones"
        :elemento-tipo="localModel.ElementoTipo"
      />
      <el-divider />
    </elemento-header>
  </el-collapse>
</template>

<script setup>
import {
  onBeforeMount,
  provide,
  ref,
  computed,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useElemento } from '@/shared/composables/useElemento.js';
import { usePricesStore } from '@/views/prices/stores/prices_store.js';
import { useUnidadImposicionProvider } from '@/shared/composables/use_unidad_imposicion.js';
import InputProcesos from '@/shared/components/procesos/input_procesos.vue';
import InputSuaje from '@/shared/components/suaje/input_suaje.vue';
import { useComponentsProps } from '@/shared/composables/use_components_props.js';
import { useAnchoMinimoMM } from '@/shared/composables/use_ancho_minimo.js';
import { useMaterialValidaAncho } from '@/shared/composables/use_material_valida_ancho.js';
import { useMinimosPliego } from '@/shared/composables/use_minimos_pliego.js';
import { Modules } from '@/enum/modules';
import { ElementoBase } from '@/enum/elemento_base';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: { type: Object, required: true },
  title: { type: String, required: true },
  name: { type: String, required: true },
});

const { localModel, collapseModel, updateModelo } = useElemento(props, emit);
const { anchoMinimoMM } = useAnchoMinimoMM(localModel);
const selectedMaterialAnchoMM = ref(0);

// Proveer valores de imposición para componentes de proceso
const imposicionProvidedValue = computed(() => ({
  EtiquetasEje: localModel.value.EtiquetasEje || 0,
  EtiquetasDesarrollo: localModel.value.EtiquetasDesarrollo || 0,
}));

// COTI-633: tamaño del pliego (en mm, el modelo lo guarda siempre en mm sin importar la
// unidad de display) para que cada proceso valide su máquina contra este mismo valor.
const margenesPliegoProvidedValue = computed(() => ({
  AnchoItem: localModel.value.AnchoItem || 0,
  LargoItem: localModel.value.LargoItem || 0,
}));

provide('currentModule', Modules.COTIZACION);
provide('anchoMinimoMM', anchoMinimoMM);
provide('currentElementoBase', ElementoBase.OFFSET);
provide('currentElemento', localModel);
provide('selectedMaterialAnchoMM', selectedMaterialAnchoMM);
provide('imposicion', imposicionProvidedValue);
provide('margenesPliego', margenesPliegoProvidedValue);
const { onUnidadImposicionChange } = useUnidadImposicionProvider(localModel);

const pricesStore = usePricesStore();
const { validarCotizacionCampos, procesoValidaAncho } = storeToRefs(pricesStore);
const { componentsProps } = useComponentsProps(ElementoBase.OFFSET, Modules.COTIZACION);
const tieneProcesoValidaAncho = computed(() => !!procesoValidaAncho.value);

const elementoTipo = computed(
  () => localModel.value?.ElementoTipo?.CodigoElementoBase === ElementoBase.OFFSET,
);

const { materialRef, hayMaterialQueValidaAncho } = useMaterialValidaAncho(localModel);

const mostrarSuaje = computed(
  () => tieneProcesoValidaAncho.value || hayMaterialQueValidaAncho.value,
);

// Los mínimos se recalculan y se vuelven a escribir en el modelo cada vez que hace falta,
// incluso cuando el elemento se recarga tras cotizar y llega sin ellos. input_suaje los lee
// del modelo para mostrarlos junto a cada medida.
useMinimosPliego(localModel, updateModelo);

onBeforeMount(() => {
  updateModelo({
    ...localModel.value,
    EtiquetasEje: localModel.value.EtiquetasEje || 1,
    EtiquetasDesarrollo: localModel.value.EtiquetasDesarrollo || 1,
  });
});
</script>

<style scoped>
.detail-container-printed {
  padding: 0;
}
.custom-label {
  font-weight: 700;
  color: #111;
}

.colors-button {
  width: 100%;
}
</style>
